import {
  MICE_NEED_ALERT_CODES,
  MICE_PHASE_BAR,
  type MiceColor,
  type MicePhaseFilter,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Dot } from '@web/ui/core';
import { twcn } from '@web/ui/core/utils';
import type { MiceTravelerFilter } from '../travelers/traveler_filter';
import { COLOR_TONE } from './color_dot';
import { PHASE_TONE } from './traveler_cells';

type AlertValue = MiceTravelerFilter['alert'];
type OnFilter = (next: Partial<MiceTravelerFilter>) => void;

const PHASES: readonly MicePhaseFilter[] = ['all', ...MICE_PHASE_BAR];
const COLORS: readonly MiceColor[] = ['green', 'yellow', 'red'];

function alertLabel(alert: AlertValue): string {
  if (alert === 'any') return t('mice.filter.anyAlert');
  if (alert === 'none') return t('mice.filter.noAlert');
  if (alert === 'profile_pending') return t('mice.filter.profilePending');
  return t(`mice.needs.alert.${alert}`);
}

interface ItemProps {
  on: boolean;
  onClick: () => void;
  label: string;
  count?: number;
  tone?: 'destructive';
  dot?: React.ReactNode;
  mono?: boolean;
}

function Item({ on, onClick, label, count, tone, dot, mono }: Readonly<ItemProps>) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={twcn(
        'flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-meta',
        on ? 'bg-muted font-medium text-foreground' : 'text-foreground-default hover:bg-muted',
        tone === 'destructive' && 'text-destructive',
        mono && 'font-mono',
      )}
    >
      <span className="flex items-center gap-2">
        {dot}
        {label}
      </span>
      {count === undefined ? null : <span className="tabular-nums">{count}</span>}
    </button>
  );
}

function Group({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <nav aria-label={label} className="flex flex-col gap-0.5">
      <span className="px-2.5 pb-1 text-eyebrow text-muted-foreground">{label}</span>
      {children}
    </nav>
  );
}

interface FiltersRailProps {
  counts: Record<MicePhaseFilter, number>;
  /** Recomendações de cada cor em "Para emitir". */
  colorCounts: Record<MiceColor, number>;
  filter: MiceTravelerFilter;
  origins: readonly string[];
  onFilter: OnFilter;
}

/** O `id` da coluna: o botão de recolher a aponta em `aria-controls`. */
export const FILTERS_RAIL_ID = 'mice-filters-rail';

/** Coluna à esquerda da lista: fase e cor (com contagem), alertas e origem. */
export function FiltersRail({
  counts,
  colorCounts,
  filter,
  origins,
  onFilter,
}: Readonly<FiltersRailProps>) {
  const alerts: AlertValue[] = ['any', 'none', 'profile_pending', ...MICE_NEED_ALERT_CODES];
  return (
    <div id={FILTERS_RAIL_ID} className="flex w-full shrink-0 flex-col gap-4 lg:w-56">
      <Group label={t('mice.phase.label')}>
        {PHASES.map((phase) => (
          <Item
            key={phase}
            on={filter.phase === phase}
            onClick={() => {
              onFilter({ phase });
            }}
            label={t(`mice.phase.${phase}`)}
            count={counts[phase]}
            tone={phase === 'attention' ? 'destructive' : undefined}
            dot={phase === 'all' ? null : <Dot tone={PHASE_TONE[phase]} size="md" />}
          />
        ))}
      </Group>
      {filter.phase === 'approval' ? (
        <Group label={t('mice.color.label')}>
          {(['any', ...COLORS] as const).map((color) => (
            <Item
              key={color}
              on={filter.color === color}
              onClick={() => {
                onFilter({ color });
              }}
              label={t(`mice.color.${color}`)}
              count={color === 'any' ? undefined : colorCounts[color]}
              dot={color === 'any' ? null : <Dot tone={COLOR_TONE[color]} size="md" />}
            />
          ))}
        </Group>
      ) : null}
      <Group label={t('mice.filter.alert')}>
        {alerts.map((alert) => (
          <Item
            key={alert}
            on={filter.alert === alert}
            onClick={() => {
              onFilter({ alert });
            }}
            label={alertLabel(alert)}
          />
        ))}
      </Group>
      <Group label={t('mice.filter.origin')}>
        {['any', ...origins].map((origin) => (
          <Item
            key={origin}
            on={filter.origin === origin}
            onClick={() => {
              onFilter({ origin });
            }}
            label={origin === 'any' ? t('mice.filter.anyOrigin') : origin}
            mono={origin !== 'any'}
          />
        ))}
      </Group>
    </div>
  );
}

/** Um filtro diferente do padrão, com o que o remove. */
function activeTags(filter: MiceTravelerFilter, onFilter: OnFilter) {
  const tags: { label: string; remove: () => void }[] = [];
  if (filter.phase !== 'all') {
    tags.push({
      label: t('mice.filter.tag.phase', { phase: t(`mice.phase.${filter.phase}`) }),
      remove: () => {
        onFilter({ phase: 'all' });
      },
    });
  }
  if (filter.color !== 'any') {
    tags.push({
      label: t('mice.filter.tag.color', { color: t(`mice.color.${filter.color}`) }),
      remove: () => {
        onFilter({ color: 'any' });
      },
    });
  }
  if (filter.alert !== 'any') {
    tags.push({
      label: t('mice.filter.tag.alert', { alert: alertLabel(filter.alert) }),
      remove: () => {
        onFilter({ alert: 'any' });
      },
    });
  }
  if (filter.origin !== 'any') {
    tags.push({
      label: t('mice.filter.tag.origin', { iata: filter.origin }),
      remove: () => {
        onFilter({ origin: 'any' });
      },
    });
  }
  return tags;
}

/** Com a coluna recolhida, os filtros ativos viram etiquetas que se removem uma a uma. */
export function ActiveFilterTags({
  filter,
  onFilter,
}: Readonly<{ filter: MiceTravelerFilter; onFilter: OnFilter }>) {
  return (
    <>
      {activeTags(filter, onFilter).map((tag) => (
        <span
          key={tag.label}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-card py-0.5 pl-2.5 pr-1 text-meta"
        >
          {tag.label}
          <button
            type="button"
            aria-label={t('mice.filter.removeTag', { label: tag.label })}
            onClick={tag.remove}
            className="rounded-full px-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            ✕
          </button>
        </span>
      ))}
    </>
  );
}
